
import { supabase } from "@/integrations/supabase/client";
import { performanceMonitor } from "./performanceMonitor";

interface EntryReassignmentResult {
  movedCount: number;
  movedEntries: any[];
  errors: string[];
}

interface ReassignmentFunctionResult {
  moved_count: number;
  moved_entries: any[];
}

export const reassignTimelineEntriesForUser = async (userId: string, birthdate: string): Promise<EntryReassignmentResult> => {
  return performanceMonitor.track('timeline_entry_reassignment', async () => {
    try {
      const { data, error } = await supabase.rpc('reassign_timeline_entries_after_birthdate_change', {
        target_user_id: userId,
        new_birthdate: birthdate
      });

      if (error) {
        console.error('Reassignment function error:', error);
        return {
          movedCount: 0,
          movedEntries: [],
          errors: [error.message]
        };
      }

      // Handle the response data properly
      const result = Array.isArray(data) && data.length > 0 ? data[0] as ReassignmentFunctionResult : null;
      
      return {
        movedCount: result?.moved_count || 0,
        movedEntries: Array.isArray(result?.moved_entries) ? result.moved_entries : [],
        errors: []
      };
    } catch (err) {
      console.error('Reassignment error:', err);
      return {
        movedCount: 0,
        movedEntries: [],
        errors: [err instanceof Error ? err.message : 'Unknown error']
      };
    }
  });
};

export const validateEntryPlacement = async (userId: string): Promise<{
  misplacedEntries: any[];
  totalEntries: number;
}> => {
  try {
    // Get user's birthdate and timeline entries with section info
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('birthdate')
      .eq('id', userId)
      .single();

    if (profileError || !profileData?.birthdate) {
      console.error('Cannot validate without birthdate:', profileError);
      return { misplacedEntries: [], totalEntries: 0 };
    }

    const birthYear = new Date(profileData.birthdate).getFullYear();
    
    // Get all entries with their section age ranges
    const { data: entries, error: entriesError } = await supabase
      .from('profile_timeline_entries')
      .select(`
        id,
        title,
        year,
        profile_timeline_section_id,
        profile_timeline_sections!inner(
          id,
          template_timeline_sections!inner(
            age_from,
            age_to,
            name
          )
        )
      `)
      .eq('user_id', userId)
      .not('year', 'is', null);

    if (entriesError) {
      console.error('Error fetching entries:', entriesError);
      return { misplacedEntries: [], totalEntries: 0 };
    }

    const misplacedEntries = entries?.filter(entry => {
      const entryAge = entry.year - birthYear;
      const section = entry.profile_timeline_sections.template_timeline_sections;
      return entryAge < section.age_from || entryAge > section.age_to;
    }) || [];
    
    return {
      misplacedEntries,
      totalEntries: entries?.length || 0
    };
  } catch (err) {
    console.error('Validation error:', err);
    return { misplacedEntries: [], totalEntries: 0 };
  }
};

export const calculateCorrectSectionForEntry = async (
  userId: string, 
  entryYear: number, 
  birthdate: string
): Promise<string | null> => {
  try {
    const birthYear = new Date(birthdate).getFullYear();
    const entryAge = entryYear - birthYear;
    
    // Find the correct section for this age
    const { data: section, error } = await supabase
      .from('profile_timeline_sections')
      .select(`
        id,
        template_timeline_sections!inner(
          age_from,
          age_to
        )
      `)
      .eq('profile_timeline_id', (
        await supabase
          .from('profile_timeline')
          .select('id')
          .eq('user_id', userId)
          .single()
      ).data?.id)
      .eq('is_active', true)
      .gte('template_timeline_sections.age_to', entryAge)
      .lte('template_timeline_sections.age_from', entryAge)
      .single();

    if (error) {
      console.error('Error finding correct section:', error);
      return null;
    }

    return section?.id || null;
  } catch (err) {
    console.error('Section calculation error:', err);
    return null;
  }
};
